import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.rf-funserver.my.id').replace(/\/+$/, '');

function formatLttm(lttmString) {
  if (!lttmString) return '-';
  const parts = lttmString.split('-');
  if (parts.length >= 6) {
    const [year, month, day, hour, minute, second] = parts;
    return `${hour}:${minute}:${second} (${day}/${month}/${year})`;
  }
  return lttmString;
}

export default function PitbossTracker() {
  const [bosses, setBosses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, ALIVE, DEAD

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/pitboss-tracker`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status !== 'success') {
        throw new Error('Data tidak tersedia');
      }
      setBosses(json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter Data
  const filteredBosses = bosses.filter((b) => {
    // Hide specific maps
    const hiddenMaps = ['cora', 'dungeon03'];
    if (hiddenMaps.includes(b.map.toLowerCase())) return false;

    const matchesSearch = b.mobcode.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          b.map.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesStatus = true;
    if (statusFilter === 'ALIVE') matchesStatus = b.live === 1;
    if (statusFilter === 'DEAD') matchesStatus = b.live === 0;
    return matchesSearch && matchesStatus;
  });

  // Group by Map
  const groupedBosses = filteredBosses.reduce((acc, boss) => {
    if (!acc[boss.map]) acc[boss.map] = [];
    acc[boss.map].push(boss);
    return acc;
  }, {});
  
  const sortedMaps = Object.keys(groupedBosses).sort();

  return (
    <div className="min-h-screen pt-20 pb-16 px-4" style={{ background: '#0A0A0A', color: '#F0F0F0' }}>
      
      {/* ─── HEADER ─── */}
      <div className="text-center py-8 max-w-4xl mx-auto">
        <p className="text-sm font-bold tracking-widest uppercase mb-1"
          style={{ color: '#FF2222', textShadow: '0 0 15px rgba(255,34,34,0.5)', fontFamily: 'monospace' }}>
          ⚔ RF FUN REBORN ⚔
        </p>
        <h1 className="text-4xl md:text-6xl font-black leading-none mb-2"
          style={{ background: 'linear-gradient(135deg,#FFF0A0,#FFD700,#B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'monospace' }}>
          PITBOSS TRACKER
        </h1>
        <p className="text-sm text-gray-500 mt-2 tracking-widest">REAL-TIME BOSS STATUS OVERVIEW</p>
        <div className="w-28 h-0.5 mx-auto mt-4"
          style={{ background: 'linear-gradient(90deg,transparent,#FFD700,transparent)' }} />
      </div>

      <div className="max-w-6xl mx-auto">
        
        {/* ─── CONTROLS ─── */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-8">
          <div className="flex gap-2">
            {['ALL', 'ALIVE', 'DEAD'].map((status) => (
              <button key={status} onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${
                  statusFilter === status 
                    ? 'border-yellow-500 bg-yellow-500/10 text-yellow-400 shadow-[0_0_15px_rgba(234,179,8,0.2)]'
                    : 'border-gray-800 bg-gray-900/50 text-gray-500 hover:border-gray-700 hover:text-gray-300'
                }`}>
                {status === 'ALL' ? 'Semua' : status === 'ALIVE' ? 'Hidup' : 'Mati'}
              </button>
            ))}
          </div>

          <div className="relative">
            <input 
              type="text" 
              placeholder="Cari Boss atau Map..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:border-yellow-500 transition-colors placeholder-gray-600"
            />
            <button onClick={fetchData} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-yellow-400 p-1" title="Refresh">
              <svg className={`w-4 h-4 ${loading ? 'animate-spin text-yellow-500' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* ─── ERROR ─── */}
        {error && !loading && (
          <div className="text-center py-10">
            <p className="text-red-400 mb-2">Gagal memuat data tracker</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        )}

        {/* ─── GRID ─── */}
        {!error && sortedMaps.length > 0 && (
          <div className="space-y-12">
            {sortedMaps.map((mapName) => (
              <div key={mapName}>
                <div className="flex items-center gap-4 mb-5">
                  <h2 className="text-2xl font-black text-white tracking-widest uppercase">
                    {mapName}
                  </h2>
                  <div className="h-px bg-gray-800 flex-grow"></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {groupedBosses[mapName].map((boss, idx) => {
                    const isAlive = boss.live === 1;
                    return (
                      <div key={idx} 
                        className="relative overflow-hidden rounded-xl p-5 transition-all duration-300 hover:-translate-y-1"
                        style={{ 
                          background: '#111', 
                          border: `1px solid ${isAlive ? 'rgba(34, 197, 94, 0.4)' : '#222'}`,
                          boxShadow: isAlive ? '0 0 20px rgba(34, 197, 94, 0.1)' : 'none'
                        }}>
                        
                        {isAlive && (
                          <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/20 blur-xl rounded-full -mr-8 -mt-8 pointer-events-none" />
                        )}

                        <div className="flex justify-end items-start mb-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded border ${
                            isAlive 
                              ? 'bg-green-500/10 text-green-400 border-green-500/30 shadow-[0_0_10px_rgba(34,197,94,0.2)]' 
                              : 'bg-red-500/10 text-red-400 border-red-500/30'
                          }`}>
                            {isAlive ? 'ALIVE' : 'DEAD'}
                          </span>
                        </div>

                        <h3 className={`font-black text-xl mb-1 leading-tight ${isAlive ? 'text-white' : 'text-gray-400'}`}>
                          {boss.mobcode}
                        </h3>
                        
                        <div className="flex items-center gap-2 mt-4 text-xs">
                          <div className={`w-1.5 h-1.5 rounded-full ${isAlive ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                          <span className="text-gray-500">Waktu:</span>
                          <span className="text-gray-300 font-mono">{formatLttm(boss.lttm)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!loading && !error && sortedMaps.length === 0 && (
          <div className="text-center py-20 text-gray-500 border border-dashed border-gray-800 rounded-xl">
            Tidak ada data Pitboss yang sesuai filter.
          </div>
        )}

      </div>
    </div>
  );
}
